<template>
    <nav class="main-nav desktop-main-nav w-full relative flex items-center">
        <router-link to="/" class="main-logo relative block">
            <div class="logo-bg">
                <img
                    class="w-auto h-full"
                    src="/images/header/logo-desktop-bg.svg?v=1.2.0"
                    alt=""
                />
            </div>
            <img
                class="pip-logo"
                src="/images/pip-global-safety-logo.svg"
                alt="PIP Global Safety"
            />
        </router-link>
        <div class="logo-placeholder"></div>
        <div class="w-full flex items-center justify-start">
            <ul class="nav-links flex items-center gap-8">
                <li
                    v-for="link in menuLinks"
                    :key="link.label"
                    class="nav-item relative"
                >
                    <a
                        v-if="link.external"
                        :href="link.href"
                        target="_blank"
                        rel="noopener noreferrer"
                        :class="navLinkClass"
                    >
                        {{ link.label }}
                    </a>
                    <span
                        v-else-if="link.href === '#'"
                        :class="['nav-void', navLinkClass]"
                        @click="toggleSubmenu(link)"
                    >
                        {{ link.label }}
                    </span>
                    <router-link
                        v-else
                        :to="link.href"
                        :class="navLinkClass"
                        @click="
                            link.children && link.children.length
                                ? toggleSubmenu(link)
                                : closeSubmenu()
                        "
                    >
                        {{ link.label }}
                    </router-link>

                    <DesktopSubmenu
                        v-if="link.children && link.children.length"
                        :menu-item="link"
                        :open="activeMenu === link.label"
                        @close="closeSubmenu"
                    />
                </li>
            </ul>
        </div>

        <div class="right-side-wrapper">
            <div
                id="right-side-menu"
                class="ml-auto d-flex justify-content-center"
            >
                <DesktopAccountMenu v-if="useAdmin" />

                <div ref="additionalMenuRoot" class="flyout-menu">
                    <button
                        type="button"
                        class="additional-menu-toggle"
                        aria-label="Toggle additional menu"
                        :aria-expanded="displayMenu ? 'true' : 'false'"
                        @click.stop="toggleDisplayMenu"
                    >
                        <i class="fas fa-bars"></i>
                    </button>
                    <AdditionalMenu :class="{ 'is-open': displayMenu }" />
                </div>

                <div
                    v-if="useProducts"
                    class="search text-light"
                    data-toggle="modal"
                    data-target="#searchPanel"
                >
                    <i class="fas fa-search mr-2"></i>
                </div>
            </div>
        </div>
    </nav>
</template>

<script setup>
import {
    computed,
    getCurrentInstance,
    onBeforeUnmount,
    onMounted,
    ref,
    watch,
} from "vue";
import AdditionalMenu from "./AdditionalMenu.vue";
import DesktopAccountMenu from "./DesktopAccountMenu.vue";
import DesktopSubmenu from "./DesktopSubmenu.vue";
import { desktopMenuLinks, visibleMenuItems } from "./menuLinks";

const { proxy } = getCurrentInstance();
const store = proxy.$store;

const activeMenu = ref(null);
const additionalMenuRoot = ref(null);
const displayMenu = ref(false);

const menuLinks = computed(() => visibleMenuItems(desktopMenuLinks));
const useAdmin = computed(() => store.state.useAdmin);
const useProducts = computed(() => store.state.useProducts);

const navLinkClass =
    "text-[17.6px] text-grey-400 font-bold uppercase py-2 block";

function toggleSubmenu(link) {
    if (!link.children || !link.children.length) {
        activeMenu.value = null;
        return;
    }
    activeMenu.value = activeMenu.value === link.label ? null : link.label;
}

function closeSubmenu() {
    activeMenu.value = null;
}

function toggleDisplayMenu() {
    displayMenu.value = !displayMenu.value;
}

function handleDocumentClick(event) {
    if (
        displayMenu.value &&
        additionalMenuRoot.value &&
        !additionalMenuRoot.value.contains(event.target)
    ) {
        displayMenu.value = false;
    }
}

watch(activeMenu, (value) => {
    if (typeof document === "undefined") return;
    document.body.style.overflow = value ? "hidden" : "";
});

onMounted(() => {
    document.addEventListener("click", handleDocumentClick);
});

onBeforeUnmount(() => {
    document.removeEventListener("click", handleDocumentClick);

    if (typeof document !== "undefined") {
        document.body.style.overflow = "";
    }
});
</script>

<style lang="scss" scoped>
.main-nav {
    --desktop-logo-width: clamp(250px, 17vw, 320px);
    --desktop-nav-gap: 2rem;

    background: white;
    gap: var(--desktop-nav-gap);
    min-height: 50px;
    position: sticky;
    top: 0;
    z-index: 1000;

    .right-side-wrapper {
        align-self: stretch;
        flex: 0 0 auto;
    }

    #right-side-menu {
        align-items: stretch;
        display: flex;
        height: 100%;
        justify-content: center;
    }

    .flyout-menu {
        align-items: center;
        align-self: stretch;
        background: #e6e6e7;
        cursor: pointer;
        display: flex;
        flex: 0 0 50px;
        justify-content: center;
        position: relative;
        text-align: center;

        .additional-menu-toggle {
            align-items: center;
            background: transparent;
            border: 0;
            cursor: pointer;
            display: flex;
            height: 100%;
            justify-content: center;
            padding: 0;
            width: 100%;
        }

        i {
            color: #333;
            font-size: 1.5rem;
        }

        #drop-menu-more {
            clip-path: inset(0 0 100% 0);
            margin: 0;
            opacity: 0;
            overflow: hidden;
            pointer-events: none;
            top: 100%;
            transform: translateY(-4px);
            transform-origin: top center;
            visibility: hidden;
            will-change: clip-path, opacity, transform;
            transition: clip-path 320ms cubic-bezier(0.4, 0, 0.2, 1),
                opacity 220ms ease, transform 320ms cubic-bezier(0.4, 0, 0.2, 1),
                visibility 0s linear 320ms;

            &.is-open {
                clip-path: inset(0 0 0 0);
                opacity: 1;
                pointer-events: auto;
                transform: translateY(0);
                visibility: visible;
                transition-delay: 0s;
            }
        }
    }

    .search {
        align-items: center;
        background: #cd1b00;
        cursor: pointer;
        display: flex;
        font-size: 1rem;
        justify-content: center;
        padding: 0 1rem;
        text-align: center;

        i {
            color: white;
            margin: 0 !important;
        }
    }

    .logo-placeholder {
        flex: 0 0 var(--desktop-logo-width);
        height: 100%;
        width: var(--desktop-logo-width);
    }

    .main-logo {
        aspect-ratio: 699 / 187.9;
        height: auto;
        left: 0;
        position: absolute;
        top: 0;
        width: var(--desktop-logo-width);
        z-index: 999;

        .logo-bg {
            height: 100%;
            position: absolute;
            right: 0;
            top: 0;
            width: auto;
            z-index: 0;

            &::before {
                background: #cd1b00;
                content: "";
                height: 100%;
                position: absolute;
                right: 100%;
                top: 0;
                width: 300px;
                z-index: -1;
            }
        }

        .pip-logo {
            box-sizing: border-box;
            height: 100%;
            object-fit: contain;
            padding: 0.5rem 2.5rem 0.75rem 0.75rem;
            position: relative;
            width: 100%;
            z-index: 1;
        }
    }

    .nav-links {
        list-style: none;

        &:hover {
            cursor: pointer;
        }

        .nav-item > a,
        .nav-item > .nav-void {
            color: #5e5e5e;
            transition: none;

            &:hover,
            &:focus,
            &:active {
                color: #5e5e5e;
                text-decoration: none;
            }
        }
    }

    .text-\[17\.6px\] {
        font-size: 17.6px;
    }
}

@media (max-width: 1199px) {
    .desktop-main-nav {
        display: none !important;
    }
}
</style>
